import { createContext, useContext, useReducer } from 'react';
import type { ReactNode } from 'react';
import { SET_AUTH_USER, USER_LOGOUT } from './actionTypes';
import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import calculateUserRoles from '../util/calculateUserRoles';
import { AUTH_TYPES } from '@/types';
import type { AuthState, AuthAction, AppRole, AuthConfig } from '@/types';

const AuthContext = createContext<AuthState | null>(null);

const AuthDispatchContext = createContext<React.Dispatch<AuthAction> | null>(
  null,
);

const auth_config: AuthConfig = {
  entity_id: '',
  tenant_domain: '',
  auth_type: AUTH_TYPES.OIDC,
  authority: import.meta.env.VITE_AUTHORITY || null,
  client_id: import.meta.env.VITE_CLIENT_ID || null,
  redirect_uri: `${import.meta.env.VITE_APP_URL}/authenticate`,
  response_type: import.meta.env.VITE_RESPONSE_TYPE || 'code',
  scope: import.meta.env.VITE_SCOPE || null,
  post_logout_redirect_uri:
    import.meta.env.VITE_APP_LOGOUT_URL ||
    `${import.meta.env.VITE_APP_URL}/logout`,
  response_mode: import.meta.env.VITE_RESPONSE_MODE || null,
  llm_model_family: null,
  vllm_model_family: null,
  api_endpoint: import.meta.env.VITE_API_URL,
  on_prem: false,
};

const settings: Record<string, unknown> = {
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  loadUserInfo: false,
  automaticSilentRenew: false,
  accessTokenExpiringNotificationTimeInSeconds: 300,
  monitorSession: true,
  authority: auth_config.authority || undefined,
  client_id: auth_config.client_id || undefined,
  redirect_uri: auth_config.redirect_uri || undefined,
  response_type: auth_config.response_type || undefined,
  scope: auth_config.scope || undefined,
  post_logout_redirect_uri: auth_config.post_logout_redirect_uri || undefined,
  response_mode: auth_config.response_mode || undefined,
};

const initialState: AuthState = {
  config: auth_config,
  user_manager: new UserManager(settings as any),
  user: undefined,
  roles: [],
};

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [auth, dispatch] = useReducer(authReducer, initialState);

  return (
    <AuthContext.Provider value={auth}>
      <AuthDispatchContext.Provider value={dispatch}>
        {children}
      </AuthDispatchContext.Provider>
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const auth = useContext(AuthContext);
  if (!auth) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return auth;
}

export function useAuthDispatch(): React.Dispatch<AuthAction> {
  const dispatch = useContext(AuthDispatchContext);
  if (!dispatch) {
    throw new Error('useAuthDispatch must be used within AuthProvider');
  }
  return dispatch;
}

function authReducer(auth: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case SET_AUTH_USER: {
      if (action.payload && action.payload?.access_token) {
        localStorage.setItem(
          'accqsure_jwt',
          action.payload && action.payload?.access_token,
        );
      }
      const roles: AppRole[] = calculateUserRoles(action.payload);
      return { ...auth, user: action.payload, roles };
    }
    case USER_LOGOUT: {
      localStorage.removeItem('accqsure_jwt');
      return { ...auth, user: null, roles: [] };
    }
    default: {
      throw Error('Unknown action: ' + JSON.stringify(action));
    }
  }
}
